import React, { useState, useEffect } from "react";
import Image from "next/legacy/image";

import { useFormik } from "formik";
import * as yup from "yup";

import TextFieldFormik from "@/ui/TextFieldFormik";
import { TextAreaFormik } from "@/ui/TextArea";
import Button from "@/ui/Button";

import HomeHeader from "@/components/home/HomeHeader";
import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import CpHomeHeader from "@/components/communityPortal/home/CpHomeHeader";
import CpHomeFooter from "@/components/communityPortal/home/CpHomeFooter";

import { imageLoader } from "@/utils/image";
import { AuthProvider, useAuth } from "@/lib/auth";
import { readDocument, updateDocument } from "@/lib/db";
import SignUpSignIn from "../user/SignUpSignIn";
import { YouNeedToRegister } from "../dialogs/utils";

const validationSchema = yup.object({
    name: yup.string().required("Name is required"),
    email: yup.string().required("Email address is required"),
    phone: yup.string().required("Phone number is required"),
    subject: yup.string().required("Subject is required"),
    message: yup.string().required("Message is required"),
});

const ContactUs = ({ communityPortal = false }) => {
    const [user, setUser] = useState({});
    const auth = useAuth();
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [complete, setComplete] = useState(false);

    const userId = auth && auth.user ? auth.user.uid : "";
    const token = auth && auth.user ? auth.user.token : "";

    const formik = useFormik({
        initialValues: {
            name: user.name,
            email: user.email,
            phone: user.phone,
            subject: "",
            message: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                if (userId && (values.name !== user.name || values.phone !== user.phone)) {
                    await updateDocument("users", user.uid, {
                        ...user,
                        name: values.name ? values.name : user.anme,
                        phone: values.phone ? values.phone : user.phone,
                    });
                }
            } catch (error) {
                //console.error(error);
            }

            const res = await fetch("/api/email/contact-us", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                    token,
                },
                body: JSON.stringify(
                    {
                        title: "Contact Us",
                        preview: `Contact Us Message from ${values.name}`,
                        name: values.name,
                        email: values.email,
                        phone: values.phone,
                        subject: values.subject,
                        message: values.message,
                    },
                    null,
                    2
                ),
            });

            gtag("event", "contact_us", {
                event_category: "engagement",
                event_label: "contact_us_form",
            });

            setComplete(true);
        },
    });

    useEffect(() => {
        const getUser = async (uid) => {
            const _user = await readDocument("users", uid);
            setUser(_user);
            formik.setFieldValue("name", _user.name);
            formik.setFieldValue("email", _user.email);
            formik.setFieldValue("phone", _user.phone);
        };

        if (userId) {
            getUser(userId);
        }
    }, [userId]);

    return (
        <div className="">
            {communityPortal ? <CpHomeHeader fixed /> : <HomeHeader fixed />}

            <div className="relative">
                <div className="md:hidden relative w-full h-72">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-contact-us"
                        alt="contact us"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="left top"
                        priority
                        unoptimized={false}
                        className=""
                    />
                </div>

                <div className="sm:pt-12 container mx-auto px-4 sm:px-16">
                    <div className="w-full md:w-1/2 pt-8 sm:pt-24 sm:pr-8 min-h-[800px]">
                        <h2 className="text-gray-sec text-2xl font-bold tracking-normal font-headline uppercase">
                            Contact Us
                        </h2>
                        <h1 className="mt-2 text-primary-500 text-4xl font-bold tracking-normal font-headline">
                            We'd Love To Hear From You!
                        </h1>

                        <p className="mt-4 text-lg">
                            Purple Door Finders is here to support you during your search
                            for senior and assisted living. Please fill out the contact form
                            below so we have the right support team member reply to you as
                            soon as possible.
                        </p>
                        <p className="mt-4 text-lg">
                            You can also reach our Support Team directly at{" "}
                            <a
                                href="mailto:support@purpledoorfinders.com"
                                className="text-primary-500 underline hover:font-bold"
                            >
                                support@purpledoorfinders.com
                            </a>
                        </p>

                        {/*userId === "" ? (
                            <div className="mt-8 pb-4 border rounded border-primary-500">
                                <YouNeedToRegister
                                    setOpenSignIn={setOpenSignIn}
                                    setOpenSignUp={setOpenSignUp}
                                    onClose={() => {}}
                                />
                            </div>
                        ) : (*/}
                        <div className="mt-4 sm:mt-8 mb-8 py-4 px-4 sm:px-8 bg-gray-100 rounded">
                            <h3 className="text-2xl font-headline font-bold text-primary-500">
                                Get in touch.
                            </h3>

                            <form onSubmit={formik.handleSubmit}>
                                <TextFieldFormik
                                    formik={formik}
                                    id="name"
                                    name="name"
                                    label="Name"
                                    required
                                    fullwidth
                                    className="mt-4"
                                />

                                <TextFieldFormik
                                    formik={formik}
                                    id="email"
                                    name="email"
                                    label="Email Address"
                                    type="email"
                                    required
                                    fullwidth
                                    className="mt-2"
                                    disabled={false}
                                />

                                <TextFieldFormik
                                    formik={formik}
                                    id="phone"
                                    name="phone"
                                    label="Phone Number"
                                    type="tel"
                                    required
                                    fullwidth
                                    className="mt-2"
                                />

                                <TextFieldFormik
                                    formik={formik}
                                    id="subject"
                                    name="subject"
                                    label="Subject"
                                    required
                                    fullwidth
                                    className="mt-2"
                                />

                                <TextAreaFormik
                                    formik={formik}
                                    id="message"
                                    name="message"
                                    label="Message"
                                    required
                                    fullwidth
                                    className="mt-2"
                                />

                                <div className="flex justify-start">
                                    {complete ? (
                                        <p className="text-xl font-bold">
                                            Thank you for your inquiry. A Purple Door
                                            Finders representative will provide you with the
                                            information you requested within 2 days.
                                        </p>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            size="lg"
                                            type="submit"
                                            className="mt-2 uppercase"
                                        >
                                            Submit
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </div>
                        {/*})*/}
                    </div>
                </div>

                <div className="hidden md:block absolute top-0 left-1/2 right-0 bottom-0">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-contact-us"
                        alt="contact us"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="left top"
                        priority
                        unoptimized={false}
                        className=""
                    />
                </div>
            </div>

            {communityPortal ? null : <SeoLinks />}
            {communityPortal ? <CpHomeFooter /> : <HomeFooter />}

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const Promise = ({ title, children }) => {
    return (
        <div>
            <h3 className="text-center text-primary-500 text-2xl font-headline font-bold uppercase">
                {title}
            </h3>
            <p className="mt-8 text-center text-lg">{children}</p>
        </div>
    );
};

const Belief = ({ title, children }) => {
    return (
        <div>
            <h3 className="text-center text-gray-sec text-3xl font-headline font-bold">
                {title}
            </h3>
            <p className="mt-4 text-center text-2xl">{children}</p>
        </div>
    );
};

export default ContactUs;
